import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { backendService } from "../../../../services/backend.service";
import type { CertificationJobMapping } from "../../../../utils/models";

/**
 * The global merged HRIS role-string table (`/jobs/certification-defaults-merged`): every role
 * string the system knows about with the job(s) and position it resolves to before any
 * department override.
 *
 * One query key for the whole screen so the two consumers — the department-group colouring in
 * `jobGroups.ts` and the derived position in `hrisMapping/derivedPosition.ts` — share a single
 * cache entry and a single request.
 */
export const CERT_JOB_DEFAULTS_QUERY_KEY = ["certJobDefaults"];

export function useCertificationDefaults() {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();

	const query = useQuery<CertificationJobMapping[]>({
		queryKey: CERT_JOB_DEFAULTS_QUERY_KEY,
		queryFn: async () => (await backendService.getCertificationDefaults()).data,
		// Global reference data: identical for every department and edited only in HRIS Mapping →
		// Roles. Without this the global default of 0 refetches on every window focus, and each
		// refetch hands back a new array → a new derivation map → a new callback, re-rendering
		// every memoized role-string row on a screen that memoizes them precisely to avoid that.
		staleTime: Number.POSITIVE_INFINITY,
	});

	const { isError } = query;

	// A failed load is otherwise invisible and silently wrong: `derivePosition` falls through to the
	// column's server default and `useJobGroups` buckets every job under "Other", both of which look
	// exactly like a successful empty response.
	useEffect(() => {
		if (!isError) return;
		showSnackbar(t("department.config.certDefaults.loadError"), "error");
	}, [isError, showSnackbar, t]);

	return query;
}
